import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock3 } from "lucide-react";
import { Reveal, LineReveal } from "../components/motion";
import CTABand from "../components/CTABand";
import { api } from "../lib/api";
import { useSEO, usePageView } from "../lib/seo";

const FILTERS = ["All", "0–6 months", "4–6 months", "7–18 months", "Toddler"];

export default function SleepHelp() {
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState("All");

  useSEO({
    title: "Sleep Help — Gentle Baby & Toddler Sleep Guides",
    description: "Practical, gentle sleep help by age and problem: regressions, naps, wake windows, bedtime battles and more — written by a certified sleep consultant.",
  });
  usePageView("/sleep-help");

  useEffect(() => {
    api.articles().then(setArticles).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (filter === "All" ? articles : articles.filter((a) => a.age_tag === filter)),
    [articles, filter]
  );

  return (
    <div data-testid="sleep-help-page">
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="overline-label mb-5">Sleep help</p>
          <LineReveal
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-ink"
            lines={["Real answers to the questions", "you're typing at 3am."]}
          />
          <Reveal delay={0.5}>
            <p className="mt-7 text-lg text-ink-soft leading-relaxed">
              Practical, gentle guidance organised by your child's age and the problem in front of you. General education only — for anything medical, your GP comes first.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2.5 mb-12" role="group" aria-label="Filter articles by age" data-testid="article-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors cursor-pointer ${
                  filter === f ? "bg-ink text-white" : "bg-card border border-ink/10 text-ink-soft hover:border-ink/30"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 0.08}>
                <Link to={`/sleep-help/${a.slug}`} data-testid={`article-card-${a.slug}`} className="card-soft p-7 block h-full group">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xs font-mono uppercase tracking-widest text-terra font-semibold">{a.age_tag}</span>
                    <span className="w-1 h-1 rounded-full bg-ink/20" />
                    <span className="text-xs text-ink-muted">{a.problem_tag}</span>
                  </div>
                  <h2 className="font-serif text-xl sm:text-[1.35rem] font-medium text-ink leading-snug mb-3 group-hover:text-terra transition-colors">{a.title}</h2>
                  <p className="text-sm text-ink-soft leading-relaxed mb-5">{a.excerpt}</p>
                  <p className="flex items-center gap-1.5 text-xs text-ink-muted">
                    <Clock3 className="w-3.5 h-3.5" /> {a.read_time}
                    <ArrowRight className="w-3.5 h-3.5 ml-auto text-terra transition-transform group-hover:translate-x-1" />
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand title="Reading helped, but you want a plan for your child?" body="The free 2-minute assessment points you to the exact support that fits — or grab the $29 guide and start tonight." />
    </div>
  );
}
